<template>
    <main class="statement-page" v-loading="loading">
        <el-alert v-if="loadError !== ''" :title="loadError" type="error" :closable="false" show-icon></el-alert>
        <template v-else-if="contextLoaded">
        <section class="statement-card">
            <header class="statement-header">
                <div class="statement-title">
                    <el-button icon="el-icon-arrow-left" @click="goBack"></el-button>
                    <div>
                        <h1>{{$t('createStatement.pageTitle')}}</h1>
                        <p>{{client.name}}</p>
                    </div>
                </div>
                <div class="statement-actions">
                    <el-button icon="el-icon-edit" @click="openEditDialog">{{$t('common.edit')}}</el-button>
                    <el-button icon="el-icon-printer" @click="printStatement">{{$t('common.print')}}</el-button>
                    <el-button type="primary" icon="el-icon-message" @click="openEmailDialog">{{$t('common.email')}}</el-button>
                </div>
            </header>

            <section class="statement-summary">
                <div><span>{{$t('createStatement.from')}}</span><strong>{{formattedStartDate}}</strong></div>
                <div><span>{{$t('createStatement.to')}}</span><strong>{{formattedEndDate}}</strong></div>
                <div><span>{{$t('createStatement.invoices')}}</span><strong>{{includedInvoices.length}}</strong></div>
                <div><span>{{$t('createStatement.balanceDue')}}</span><strong>{{money(balanceDue)}}</strong></div>
            </section>

            <el-alert v-if="onlyShowUnpaid" :title="$t('createStatement.unpaidOnlyActive')" type="info" :closable="false" show-icon></el-alert>
            <el-alert v-if="previewError !== ''" :title="previewError" type="error" :closable="false" show-icon></el-alert>
            <div v-else-if="previewUrl !== ''" v-loading="previewLoading">
                <iframe v-if="previewBlobUrl !== ''" :src="previewBlobUrl" class="statement-preview"
                        frameborder="0" @load="previewLoading = false"></iframe>
            </div>
        </section>

        <el-dialog :title="$t('createStatement.editTitle')" :visible.sync="editVisible" width="560px"
                   custom-class="statement-edit-dialog"
                   :close-on-click-modal="false" @closed="resetDraftOptions">
            <el-form label-position="top">
                <div class="statement-date-grid">
                    <el-form-item :label="$t('createStatement.startDate')">
                        <el-date-picker v-model="draftStartDate" type="date" :picker-options="startDatePickerOptions"
                                        style="width: 100%"></el-date-picker>
                    </el-form-item>
                    <el-form-item :label="$t('createStatement.endDate')">
                        <el-date-picker v-model="draftEndDate" type="date" :picker-options="endDatePickerOptions"
                                        style="width: 100%"></el-date-picker>
                    </el-form-item>
                </div>
                <el-checkbox v-model="draftOnlyShowUnpaid">{{$t('createStatement.onlyShowUnpaid')}}</el-checkbox>
            </el-form>
            <div slot="footer">
                <el-button @click="editVisible = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" @click="applyStatementOptions">{{$t('common.save')}}</el-button>
            </div>
        </el-dialog>

        <el-dialog :title="$t('common.sendEmail')" :visible.sync="emailVisible" width="640px"
                   custom-class="document-email-dialog statement-email-dialog" :close-on-click-modal="false">
            <div class="document-email-form">
                <div class="document-email-field">
                    <label>{{$t('invoiceCreate.labelTo')}}</label>
                    <el-input v-model="emailForm.toEmail"></el-input>
                </div>
                <div class="document-email-row">
                    <div class="document-email-field"><label>{{$t('invoiceCreate.cc')}}</label><el-input v-model="emailForm.ccEmail"></el-input></div>
                    <div class="document-email-field"><label>{{$t('invoiceCreate.bcc')}}</label><el-input v-model="emailForm.bccEmail"></el-input></div>
                </div>
                <div class="document-email-field"><label>{{$t('common.subject')}}</label><el-input v-model="emailForm.subject"></el-input></div>
                <div class="document-email-field"><label>{{$t('default.emailMessage')}}</label><el-input v-model="emailForm.content" type="textarea" :rows="6"></el-input></div>
                <div class="document-email-attachment">
                    <div class="document-email-pdf-icon">PDF</div>
                    <div><strong>{{attachmentName}}</strong><span>{{$t('invoiceCreate.pdf')}}</span></div>
                </div>
            </div>
            <div slot="footer" class="document-email-footer">
                <el-button :disabled="emailSending" @click="emailVisible = false">{{$t('common.cancel')}}</el-button>
                <el-button type="primary" :loading="emailSending" @click="sendEmail">{{$t('common.send')}}</el-button>
            </div>
        </el-dialog>
        </template>
    </main>
</template>

<script>
    import moment from 'moment-timezone'
    import {timest, validateEmail} from '@/utils/validate'
    import {xiaoshudianfenge} from '@/utils/moneyFormat'
    import {getBrowserTimeZone} from '@/utils/browserTimeZone'
    import {syncCompanySelection} from '@/utils/company'

    export default {
        data() {
            const endDate = moment().startOf('day')
            return {
                loading: true,
                loadError: '',
                contextLoaded: false,
                previewLoading: true,
                previewBlobUrl: '',
                previewError: '',
                previewRequestSequence: 0,
                client: {innerUUID: '', name: '', clientEmail: ''},
                invoices: [],
                startDate: endDate.clone().subtract(3, 'months').toDate(),
                endDate: endDate.toDate(),
                onlyShowUnpaid: false,
                draftStartDate: endDate.clone().subtract(3, 'months').toDate(),
                draftEndDate: endDate.toDate(),
                draftOnlyShowUnpaid: false,
                editVisible: false,
                emailVisible: false,
                emailSending: false,
                emailForm: {toEmail: '', ccEmail: '', bccEmail: '', subject: '', content: ''},
                startDatePickerOptions: {
                    disabledDate: date => this.draftEndDate != null
                        && moment(date).startOf('day').isAfter(moment(this.draftEndDate).startOf('day'))
                },
                endDatePickerOptions: {
                    disabledDate: date => this.draftStartDate != null
                        && moment(date).startOf('day').isBefore(moment(this.draftStartDate).startOf('day'))
                },
                userSetting: null,
                userCompany: null
            }
        },
        computed: {
            formattedStartDate() { return moment(this.startDate).format('MMM D, YYYY') },
            formattedEndDate() { return moment(this.endDate).format('MMM D, YYYY') },
            startTimestamp() { return moment(this.startDate).startOf('day').unix() },
            endExclusiveTimestamp() { return moment(this.endDate).startOf('day').add(1, 'day').unix() },
            includedInvoices() {
                return this.invoices.filter(invoice => {
                    const start = Number(invoice.invoiceStartDate)
                    return invoice.syncStatus !== 1
                        && start >= this.startTimestamp
                        && start < this.endExclusiveTimestamp
                        && (!this.onlyShowUnpaid || invoice.paidStatus !== 2)
                })
            },
            balanceDue() {
                return this.includedInvoices.reduce((sum, invoice) => this.$NP.plus(sum, Number(invoice.balanceDueMoney)), 0)
            },
            previewUrl() {
                if (this.client.innerUUID === '') return ''
                const loginInformation = JSON.parse(localStorage.getItem('loginInformation')).objectData
                const url = new URL('preview_pdf', this.$store.state.requestURL)
                url.searchParams.set('clientUUID', this.client.innerUUID)
                url.searchParams.set('companyUUID', this.userCompany.innerUUID)
                url.searchParams.set('email', loginInformation.email)
                url.searchParams.set('userUuid', loginInformation.userUuid)
                url.searchParams.set('nowTime', timest())
                url.searchParams.set('createType', '10')
                url.searchParams.set('timeont', getBrowserTimeZone())
                url.searchParams.set('startInvoiceDate', String(this.startTimestamp))
                url.searchParams.set('endInvoiceDate', String(this.endExclusiveTimestamp))
                url.searchParams.set('onlyShowUnpaidInvoice', this.onlyShowUnpaid ? '1' : '0')
                url.searchParams.set('language', this.$i18n.locale)
                url.searchParams.set('requestId', String(Date.now()))
                return url.toString()
            },
            attachmentName() {
                const safeName = this.userCompany.companyName.replace(/[^A-Za-z0-9_-]/g, '-')
                return `Statement_from_${safeName}.pdf`
            }
        },
        methods: {
            async load(clientUUID) {
                if (clientUUID == null || clientUUID === '') {
                    throw new Error('Client UUID is required')
                }
                const contextResponse = await this.$axios.post('query_setting_and_company', {params: {}})
                if (contextResponse.data.errorCode !== 0) {
                    throw new Error('Company and setting query failed')
                }
                const selectedData = syncCompanySelection(contextResponse.data)
                if (selectedData == null) {
                    throw new Error('Selected company data is missing')
                }
                this.userCompany = selectedData.company
                this.userSetting = selectedData.setting
                this.contextLoaded = true
                const clientResponse = await this.$axios.post('query_user_client', {params: {
                    innerUUID: clientUUID,
                    companyUUID: this.userCompany.innerUUID
                }})
                if (clientResponse.data.errorCode !== 0 || clientResponse.data.objectData == null
                    || clientResponse.data.objectData.innerUUID !== clientUUID
                    || clientResponse.data.objectData.companyUUID !== this.userCompany.innerUUID
                    || clientResponse.data.objectData.syncStatus === 1
                    || clientResponse.data.objectData.isDelete !== 0) {
                    throw new Error('Client response is invalid')
                }
                this.client = clientResponse.data.objectData
                await this.loadAllInvoices()
            },
            loadRouteClient(clientUUID) {
                const endDate = moment().startOf('day')
                this.loading = true
                this.loadError = ''
                this.contextLoaded = false
                this.previewLoading = true
                this.clearPreview()
                this.client = {innerUUID: '', name: '', clientEmail: ''}
                this.invoices = []
                this.userSetting = null
                this.userCompany = null
                this.startDate = endDate.clone().subtract(3, 'months').toDate()
                this.endDate = endDate.toDate()
                this.onlyShowUnpaid = false
                this.resetDraftOptions()
                this.editVisible = false
                this.emailVisible = false
                this.load(clientUUID).then(() => {
                    this.refreshPreview()
                }).catch(() => {
                    this.loadError = this.$t('common.loadFailed')
                }).finally(() => {
                    this.loading = false
                })
            },
            async loadAllInvoices() {
                const pageSize = 400
                let currentPage = 1
                let totalCount = 0
                const invoices = []
                do {
                    const response = await this.$axios.post('query_invoice_list_user_web', {params: {
                        currentPage, pageSize, numberOrClient: '', orderName: 'invoiceStartDate', orderTpe: 2,
                        type: 0, clientUUID: this.client.innerUUID, companyUUID: this.userCompany.innerUUID,
                        timeZone: getBrowserTimeZone()
                    }})
                    if (response.data.errorCode !== 0 || !Array.isArray(response.data.dataList)
                        || !Number.isInteger(response.data.totalCount) || response.data.totalCount < 0) {
                        throw new Error('Invoice list response is invalid')
                    }
                    totalCount = response.data.totalCount
                    if (response.data.dataList.length === 0 && invoices.length < totalCount) {
                        throw new Error('Invoice list pagination response is invalid')
                    }
                    invoices.push(...response.data.dataList)
                    currentPage += 1
                } while (invoices.length < totalCount)
                this.invoices = invoices
            },
            applyStatementOptions() {
                if (this.draftStartDate == null || this.draftEndDate == null) {
                    this.$message.error(this.$t('createStatement.dateRequired'))
                    return
                }
                if (moment(this.draftEndDate).isBefore(moment(this.draftStartDate), 'day')) {
                    this.$message.error(this.$t('createStatement.invalidDateRange'))
                    return
                }
                this.startDate = this.draftStartDate
                this.endDate = this.draftEndDate
                this.onlyShowUnpaid = this.draftOnlyShowUnpaid
                this.previewLoading = true
                this.editVisible = false
                this.refreshPreview()
            },
            openEditDialog() {
                this.resetDraftOptions()
                this.editVisible = true
            },
            resetDraftOptions() {
                this.draftStartDate = this.startDate
                this.draftEndDate = this.endDate
                this.draftOnlyShowUnpaid = this.onlyShowUnpaid
            },
            async fetchPdf() {
                const response = await fetch(this.previewUrl, {method: 'GET', cache: 'no-store', credentials: 'omit'})
                if (!response.ok) throw new Error(`${this.$t('common.pdfPreparationFailed')} (HTTP ${response.status})`)
                const pdf = await response.blob()
                if (await pdf.slice(0, 5).text() !== '%PDF-') throw new Error(this.$t('common.pdfPreparationFailed'))
                return pdf
            },
            clearPreview() {
                this.previewRequestSequence += 1
                if (this.previewBlobUrl !== '') {
                    URL.revokeObjectURL(this.previewBlobUrl)
                    this.previewBlobUrl = ''
                }
                this.previewError = ''
            },
            async refreshPreview() {
                const requestSequence = this.previewRequestSequence + 1
                this.previewRequestSequence = requestSequence
                if (this.previewBlobUrl !== '') {
                    URL.revokeObjectURL(this.previewBlobUrl)
                    this.previewBlobUrl = ''
                }
                this.previewError = ''
                this.previewLoading = true
                try {
                    const pdf = await this.fetchPdf()
                    if (requestSequence !== this.previewRequestSequence) {
                        return
                    }
                    this.previewBlobUrl = URL.createObjectURL(pdf)
                } catch (error) {
                    if (requestSequence === this.previewRequestSequence) {
                        this.previewError = error.message
                    }
                } finally {
                    if (requestSequence === this.previewRequestSequence) {
                        this.previewLoading = false
                    }
                }
            },
            async printStatement() {
                const loading = this.$loading({lock: true, text: this.$t('common.preparingPdf')})
                try {
                    const pdf = await this.fetchPdf()
                    const url = URL.createObjectURL(pdf)
                    const frame = document.createElement('iframe')
                    frame.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;border:0'
                    frame.onload = () => { frame.contentWindow.focus(); frame.contentWindow.print(); window.setTimeout(() => { URL.revokeObjectURL(url); frame.remove() }, 60000) }
                    frame.src = url
                    document.body.appendChild(frame)
                } catch (error) {
                    this.$message.error(error.message)
                } finally {
                    loading.close()
                }
            },
            async openEmailDialog() {
                const loading = this.$loading({lock: true, text: this.$t('common.preparingPdf')})
                try {
                    await this.fetchPdf()
                    this.emailForm = {
                        toEmail: this.client.clientEmail,
                        ccEmail: this.userSetting.emailCc,
                        bccEmail: this.userSetting.emailBcc,
                        subject: `Statement from ${this.userCompany.companyName}`,
                        content: this.userSetting.emailMessage
                    }
                    this.emailVisible = true
                } catch (error) {
                    this.$message.error(error.message)
                } finally {
                    loading.close()
                }
            },
            async sendEmail() {
                if (!validateEmail(this.emailForm.toEmail)) {
                    this.$message.error(this.$t('common.validEmail'))
                    return
                }
                this.emailSending = true
                try {
                    const response = await this.$axios.post('send_email_to_client', {params: {
                        clientUUID: this.client.innerUUID,
                        companyUUID: this.userCompany.innerUUID,
                        toEmail: this.emailForm.toEmail,
                        subject: this.emailForm.subject,
                        copyEmail: this.emailForm.ccEmail,
                        secretEmail: this.emailForm.bccEmail,
                        content: this.emailForm.content,
                        pdfName: this.attachmentName,
                        timeont: getBrowserTimeZone(),
                        nowTime: timest(),
                        createType: 10,
                        startInvoiceDate: this.startTimestamp,
                        endInvoiceDate: this.endExclusiveTimestamp,
                        onlyShowUnpaidInvoice: this.onlyShowUnpaid ? 1 : 0
                    }})
                    if (response.data.errorCode === 308 || response.data.errorCode === 311) {
                        return
                    }
                    if (response.data.errorCode !== 0) {
                        throw new Error('Statement email send failed')
                    }
                    this.emailVisible = false
                    this.$message.success(this.$t('common.sendSuccess'))
                } catch {
                    this.$message.error(this.$t('common.systemError'))
                } finally {
                    this.emailSending = false
                }
            },
            goBack() { this.$router.push({path: '/clientUpdate', query: {innerUUID: this.client.innerUUID}}) },
            money(value) { return xiaoshudianfenge(value, this.userSetting) }
        },
        watch: {
            '$route.query.clientUUID'(clientUUID, previousClientUUID) {
                if (clientUUID !== previousClientUUID) {
                    this.loadRouteClient(clientUUID)
                }
            }
        },
        mounted() {
            this.loadRouteClient(this.$route.query.clientUUID)
        },
        beforeDestroy() {
            this.clearPreview()
        }
    }
</script>

<style scoped>
    .statement-page { min-height: 100%; padding: 30px; box-sizing: border-box; background: #f4f7fb; }
    .statement-card { max-width: 1260px; margin: 0 auto; padding: 0 28px 28px; background: #fff; border: 1px solid #e4eaf3; border-radius: 16px; box-shadow: 0 12px 34px rgba(41,62,96,.08); }
    .statement-header { display: flex; justify-content: space-between; align-items: center; min-height: 94px; border-bottom: 1px solid #e8edf5; }
    .statement-title { display: flex; align-items: center; gap: 16px; }
    .statement-title h1 { margin: 0; color: #17213c; font-size: 24px; }
    .statement-title p { margin: 5px 0 0; color: #8492a8; }
    .statement-actions { display: flex; gap: 10px; }
    .statement-summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; padding: 24px 0; }
    .statement-summary div { padding: 18px; background: #f7f9fd; border: 1px solid #e5ebf3; border-radius: 11px; }
    .statement-summary span, .statement-summary strong { display: block; }
    .statement-summary span { color: #8290a5; font-size: 13px; }
    .statement-summary strong { margin-top: 7px; color: #263653; font-size: 19px; }
    .statement-preview { width: 100%; height: 760px; margin-top: 18px; border: 1px solid #e4eaf3; border-radius: 10px; }
    .statement-date-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }

    /deep/ .statement-email-dialog {
        max-height: 90vh;
        margin-top: 5vh !important;
        display: flex;
        flex-direction: column;
        border-radius: 14px;
        overflow: hidden;
    }

    /deep/ .statement-email-dialog .el-dialog__header {
        padding: 24px 28px 18px;
        border-bottom: 1px solid #e8edf5;
    }

    /deep/ .statement-email-dialog .el-dialog__title {
        color: #17213c;
        font-size: 21px;
        font-weight: 700;
    }

    /deep/ .statement-email-dialog .el-dialog__body {
        padding: 20px 28px 8px;
        overflow-y: auto;
    }

    /deep/ .statement-email-dialog .el-dialog__footer {
        padding: 18px 28px 24px;
    }

    .document-email-form,
    .document-email-field {
        display: flex;
        flex-direction: column;
    }

    .document-email-form { gap: 14px; }
    .document-email-field { gap: 8px; min-width: 0; }
    .document-email-field label { color: #52627c; font-size: 13px; font-weight: 700; }
    .document-email-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }

    /deep/ .statement-email-dialog .el-input__inner,
    /deep/ .statement-email-dialog .el-textarea__inner {
        border-color: #dce4f0;
        border-radius: 8px;
        color: #263653;
        font-family: inherit;
    }

    /deep/ .statement-email-dialog .el-input__inner { height: 42px; }

    .document-email-attachment {
        min-height: 64px;
        padding: 12px 14px;
        display: flex;
        align-items: center;
        gap: 12px;
        box-sizing: border-box;
        background: #f7f9fd;
        border: 1px solid #e4eaf3;
        border-radius: 10px;
    }

    .document-email-pdf-icon {
        width: 38px;
        height: 42px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex: 0 0 38px;
        color: #ffffff;
        background: #ef4444;
        border-radius: 6px;
        font-size: 10px;
        font-weight: 800;
    }

    .document-email-attachment strong,
    .document-email-attachment span { display: block; }
    .document-email-attachment strong { color: #263653; font-size: var(--responsive-font-14); line-height: 20px; overflow-wrap: anywhere; }
    .document-email-attachment span { margin-top: 2px; color: #8997ad; font-size: 12px; }
    .document-email-footer { display: flex; justify-content: flex-end; gap: 10px; }

    @media (max-width: 800px) {
        .statement-page { padding: 14px; }
        .statement-card { padding: 0 18px 18px; }
        .statement-header { align-items: flex-start; flex-direction: column; padding: 20px 0; gap: 16px; }
        .statement-actions { flex-wrap: wrap; }
        .statement-summary { grid-template-columns: 1fr 1fr; }
        .statement-summary strong { overflow-wrap: anywhere; }
    }

    @media (max-width: 640px) {
        /deep/ .statement-edit-dialog,
        /deep/ .statement-email-dialog {
            width: calc(100% - 24px) !important;
            margin-top: 6vh !important;
        }

        .statement-date-grid,
        .document-email-row {
            grid-template-columns: 1fr;
        }
    }

    @media (max-width: 420px) {
        .statement-summary { grid-template-columns: 1fr; }
    }
</style>
